import { type CSSProperties, useContext, useEffect, useId, useMemo, useRef } from 'react';

import classnames from 'classnames';
import { DOMParser as NodeDOMParser } from 'linkedom';

import { ClientOnly } from '@/ClientOnly';
import { DreamComponentsContext } from '@/dreamComponentProvider/context';

import type { EmbedElementProps } from './Embed.types';
import { embedHeightScripts, TAILWIND_BROWSER_SCRIPT } from './embedDocument';

// Extract generated CSS block from processed html attribute
function extractGeneratedCSS(html: string | undefined): string {
  if (!html) return '';

  const cssMatch = html.match(/<!-- BEGIN GENERATED BLOCK -->([\s\S]*?)<!-- END GENERATED BLOCK -->/);
  return cssMatch ? cssMatch[1].trim() : '';
}

// Process script blocks with context-aware sandboxing
// Returns both the processed HTML and whether scripts were found
function processScriptBlocks(
  _id: string,
  htmlOriginal: string,
  isInEditor: boolean,
  fullViewport = false,
): { html: string; hasScripts: boolean } {
  // Use linkedom's DOMParser on server, native DOMParser in browser
  const Parser = typeof window !== 'undefined' ? window.DOMParser : NodeDOMParser;
  const parser = new Parser();
  const doc = parser.parseFromString(htmlOriginal, 'text/html') as Document;
  const scripts = doc.querySelectorAll('script');

  // Check for ANY elements with on* event handler attributes (onclick, onerror, onload, etc.)
  // This catches XSS attempts like: <img src=x onerror=alert(document.cookie)>
  const elementsWithEventHandlers = Array.from(doc.querySelectorAll('*')).filter((el: Element) => {
    return Array.from(el.attributes).some((attr) => attr.name.toLowerCase().startsWith('on'));
  });

  const hasScripts = scripts.length > 0 || elementsWithEventHandlers.length > 0;

  // Full HTML documents (<!DOCTYPE html>, <html>) are standalone pages that must always
  // be iframe-wrapped to prevent their styles/fonts from bleeding into the parent page (WEB-7670)
  // Only check the first 50 chars to avoid false positives on embeds showing HTML snippets as content
  const htmlStart = htmlOriginal.slice(0, 50);
  const isFullDocument = /<html[\s>]/i.test(htmlStart);

  if (!hasScripts && !isFullDocument) return { html: htmlOriginal, hasScripts: false };

  // The no-iframe directive always takes precedence — it's an advanced escape hatch
  // and users accept responsibility for style conflicts when using it
  const noIframe = htmlOriginal.match(/<!--\s*beehiiv:no-iframe\s*-->/);
  if (noIframe) {
    // Wrap scripts in template tags (prevents execution in editor)
    scripts.forEach((script) => {
      const template = doc.createElement('template');
      template.setAttribute('data-html-embed', 'true');
      template.innerHTML = script.outerHTML;
      script.replaceWith(template);
    });
    return { html: doc.body.innerHTML.replace(noIframe[0], ''), hasScripts: true };
  }

  // Wrap in iframe
  // Include embed ID for sandboxed iframes (editor) where frameElement access is blocked
  // Published sites can still use event.source.frameElement as fallback
  const iframe = doc.createElement('iframe') as HTMLIFrameElement;

  const heightScripts = embedHeightScripts(fullViewport, _id);

  const preamble = `<html>
    <head>
      ${TAILWIND_BROWSER_SCRIPT}${heightScripts.head}
    </head>
    <body>`;

  const postscript = `${heightScripts.tail}
  </body></html>`;

  // Use setAttribute to ensure consistent formatting between server (linkedom) and browser
  // Browser normalizes to "width: 100%;" but linkedom doesn't add the space/semicolon.
  iframe.setAttribute('style', fullViewport ? 'width: 100%; height: 100dvh; border: none;' : 'width: 100%;');

  // On server (linkedom), we need to manually HTML-encode the srcdoc content
  // to match what browsers do automatically. This prevents hydration mismatches.
  const srcdocContent = `${preamble}${htmlOriginal}${postscript}`;
  if (typeof window === 'undefined') {
    // Server: Manually HTML-encode to match browser behavior
    const encoded = srcdocContent
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    iframe.setAttribute('srcdoc', encoded);
  } else {
    // Browser: Setting srcdoc property auto-encodes
    iframe.setAttribute('srcdoc', srcdocContent);
  }

  // Apply sandbox based on context
  // Editor: Allow scripts to run for interactive widgets but keep isolated from parent
  // Published site: No sandbox (full access - that's the feature!)
  if (isInEditor) {
    // No allow-same-origin: a srcdoc frame would inherit the app's origin, letting the embed act
    // as the signed-in user. Widgets that need cookies or storage may not work in the preview.
    iframe.setAttribute('sandbox', 'allow-scripts');
  }

  return { html: iframe.outerHTML, hasScripts: true };
}

export function EmbedElement(props: EmbedElementProps) {
  const { id, element, attributes, className, isInEditor } = props;
  const {
    customAttributes,
    padding,
    backgroundColor,
    borderWidth,
    borderRadius,
    borderStyle,
    borderColor,
    width,
    maxWidth,
    html,
    htmlOriginal,
    timestamp,
  } = element.attrs || {};
  const ref = useRef<HTMLDivElement>(null);
  const reactId = useId();
  // Use provided id prop or generate stable one with useId (for DOM element ID)
  const embedId = id || reactId;
  // Use TipTap node UUID from attrs (for external embed URL)
  const nodeId = element.attrs.id;
  const noIframe = htmlOriginal?.match(/<!--\s*beehiiv:no-iframe\s*-->/);

  const context = useContext(DreamComponentsContext);
  const externalEmbedEnabled = context?.externalEmbedEnabled ?? false;
  const externalEmbedUrl = context?.externalEmbedUrl ?? '';
  const fullViewport = context?.rawHtmlPage ?? false;
  const isEditorCanvas = context?.isEditorCanvas ?? false;
  const isInApp = isInEditor ?? isEditorCanvas;

  // Extract generated CSS (cached from save time)
  const generatedCSS = useMemo(() => extractGeneratedCSS(html), [html]);

  // Detect if content needs iframe wrapping:
  // 1. Has scripts (and no beehiiv:no-iframe directive)
  // 2. Is a full HTML document (<html> tag) — always iframe-wrapped to prevent style bleeding (WEB-7670)
  const needsIframeWrap = useMemo(() => {
    const htmlToCheck = htmlOriginal || html || '';
    if (!htmlToCheck) return false;

    // The no-iframe directive always takes precedence — advanced users accept
    // responsibility for style conflicts when using it
    const hasNoIframe = /<!--\s*beehiiv:no-iframe\s*-->/.test(htmlToCheck);
    if (hasNoIframe) return false;

    // Full HTML documents must always be iframe-wrapped
    // Only check start of content to avoid false positives on embeds showing HTML snippets
    if (/<html[\s>]/i.test(htmlToCheck.slice(0, 50))) return true;

    // Check for scripts
    const hasScripts = /<script/i.test(htmlToCheck) || /\son\w+\s*=/i.test(htmlToCheck);

    return hasScripts;
  }, [htmlOriginal, html]);

  // Only use external embed if:
  // 1. Feature is enabled
  // 2. The page is raw HTML, so embed blocks stay in srcdoc and keep self-sizing
  // 3. Node has a UUID (nodeId) AND timestamp (saved to database)
  // 4. Content needs iframe wrapping (has scripts)
  // This prevents breaking grandfathered content that has UUIDs but no database records
  const useExternalEmbed = externalEmbedEnabled && fullViewport && nodeId && timestamp && needsIframeWrap;
  // Process htmlOriginal at render time
  //
  // Hydration Strategy:
  // - Always process HTML on both server and client (generates consistent output)
  // - For iframe-wrapped content: Use <ClientOnly> wrapper to delay rendering until after hydration
  //   * Server: Renders empty placeholder
  //   * Client first render: Matches server (empty placeholder) - NO HYDRATION ERROR
  //   * Client after hydration: useHydrated() triggers re-render with full iframe content
  // - For simple HTML (no scripts): Render normally on server and client (good for SEO)
  //
  // This approach avoids hydration mismatches caused by:
  // - linkedom (server) vs browser DOM differences in attribute ordering
  // - Different HTML encoding behavior between server and client
  // - Unpredictable user HTML that can't be consistently processed
  const processedHTML = useMemo(() => {
    // If htmlOriginal is empty, fall back to html attribute (for backwards compatibility)
    const htmlToProcess = htmlOriginal || html || '';
    if (!htmlToProcess) return '';

    // During SSR, process as published site (no sandbox)
    const { html: processed } = processScriptBlocks(embedId, htmlToProcess, false, fullViewport);
    const finalHTML = generatedCSS ? `${generatedCSS}\n${processed}` : processed;

    return finalHTML;
  }, [embedId, htmlOriginal, html, generatedCSS, needsIframeWrap, fullViewport]);

  useEffect(() => {
    if (ref.current) {
      // Embed nodes are detected by their NodeView wrapper; globals rendered outside the
      // document (navbar/footer previews) have to say so explicitly. A builder canvas with no
      // NodeView (Studio's preview) still runs inside the app, so it sandboxes like the editor.
      const isEditorContext =
        isInEditor ?? (isEditorCanvas || ref.current.closest('[data-node-view-wrapper]') !== null);

      // Use htmlOriginal if present, otherwise fall back to html (for backwards compatibility)
      const htmlToProcess = htmlOriginal || html || '';

      if (isEditorContext) {
        // If using external embed, skip processing (let the external iframe render)
        if (useExternalEmbed) {
          return;
        }

        // Check if HTML has scripts/event handlers
        const { html: processed, hasScripts } = processScriptBlocks(embedId, htmlToProcess, true, fullViewport);

        // Only show placeholders for no-iframe content (which has no sandbox protection)
        // Regular script content will render in sandboxed iframe
        if (hasScripts && noIframe) {
          // Check if the ORIGINAL HTML has visible content (before iframe wrapping)
          // Use DOMParser instead of innerHTML to avoid executing event handlers
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlToProcess, 'text/html');

          // Remove all script tags before checking for visible text
          // (script contents would otherwise appear as innerText)
          doc.querySelectorAll('script').forEach((script) => {
            script.remove();
          });

          const visibleText = doc.body.innerText?.trim() || '';

          if (visibleText) {
            // Has visible content - show the text with a notice about scripts
            ref.current.innerHTML = `<div class="border-2 border-blue-300 border-dotted bg-blue-50 text-black p-4">
              <div class="text-xs text-blue-600 font-semibold mb-2">⚠️ Scripts detected and not executed in editor</div>
              <div class="text-sm">${visibleText}</div>
            </div>`;
          } else {
            // Script-only, no visible content - show placeholder
            ref.current.innerHTML = `<div class="border-2 border-red-300 border-dotted text-center bg-white text-black p-4">Your HTML embed appears to be <code class="font-bold">&lt;script&gt;</code> only.
            Scripts are not executed in the editor, but will render when published. You can also Preview to verify your HTML embed.</div>`;
          }
        } else if (hasScripts && !noIframe) {
          // Regular sandboxed iframe - render with sandbox attribute
          const finalHTML = generatedCSS ? `${generatedCSS}\n${processed}` : processed;
          ref.current.innerHTML = finalHTML;
        } else if (!hasScripts) {
          // No scripts - render normally
          const finalHTML = generatedCSS ? `${generatedCSS}\n${processed}` : processed;
          ref.current.innerHTML = finalHTML;
        }
        // If hasScripts && !noIframe: do nothing, let dangerouslySetInnerHTML render sandboxed iframe
        return;
      }

      // Published site (not in editor)
      // Only handle noIframe case - regular iframes use dangerouslySetInnerHTML
      if (noIframe && !ref.current.dataset.rendered) {
        // Remount scripts for noIframe embeds (they need manual script execution)
        const finalHTML = generatedCSS ? `${generatedCSS}\n${htmlToProcess}` : htmlToProcess;
        ref.current.innerHTML = finalHTML;
        remountScripts(embedId);
        ref.current.dataset.rendered = 'true';
      }
    }
  }, [embedId, htmlOriginal, html, generatedCSS, noIframe, fullViewport, isInEditor, isEditorCanvas, useExternalEmbed]);

  // Use dangerouslySetInnerHTML for initial render to prevent hydration mismatch
  // useEffect will update innerHTML ONLY for editor context (where we need sandboxing)
  // Published sites render once via dangerouslySetInnerHTML and stay that way
  // no-iframe blocks are client-only (no SSR) to avoid hydration issues
  // Inside the app the effect draws the only copy, so the unsandboxed published markup never
  // reaches the DOM, even for a moment.

  const EmbedDiv = useExternalEmbed ? (
    <div
      id={embedId}
      ref={ref}
      data-embed="true"
      {...customAttributes}
      className={classnames(className, customAttributes?.className)}
      style={
        {
          position: 'relative',
          padding: padding,
          backgroundColor: backgroundColor,
          borderWidth: borderWidth,
          borderRadius: borderRadius,
          borderStyle: borderStyle,
          borderColor: borderColor,
          width: width,
          maxWidth: maxWidth,
          whiteSpace: 'normal',
        } as CSSProperties
      }
      {...attributes}
    >
      <iframe
        src={`${externalEmbedUrl}/embed/${nodeId}/${timestamp}?full=1`}
        style={{ width: '100%', height: '100dvh', border: 'none' }}
        // Security: Cross-origin iframe with publication-scoped subdomain
        // - allow-scripts: Enables interactive widgets
        // - allow-same-origin: Allows widget to access its own cookies/localStorage
        //   (scoped to publication subdomain, e.g., kiliman.beehiiv.com.beehiivusercontent.com)
        // - Parent document is STILL protected by cross-origin policy (different domain)
        // - Each publication's embeds are isolated from other publications
        sandbox="allow-scripts allow-same-origin"
        title="HTML Embed"
      />
    </div>
  ) : (
    // Regular embed: use dangerouslySetInnerHTML
    <div
      id={embedId}
      ref={ref}
      data-embed="true"
      {...customAttributes}
      className={classnames(className, customAttributes?.className)}
      style={
        {
          position: 'relative',
          padding: padding,
          backgroundColor: backgroundColor,
          borderWidth: borderWidth,
          borderRadius: borderRadius,
          borderStyle: borderStyle,
          borderColor: borderColor,
          width: width,
          maxWidth: maxWidth,
          whiteSpace: 'normal',
        } as CSSProperties
      }
      {...attributes}
      dangerouslySetInnerHTML={noIframe || isInApp ? undefined : { __html: processedHTML }}
    />
  );

  if (needsIframeWrap) {
    return <ClientOnly>{() => EmbedDiv}</ClientOnly>;
  }
  return EmbedDiv;
}

// This function remounts any <script> tags are part of the HTML embed.
// The browser will not execute <script> tags that are added via innerHTML,
// on the client. And we can't SSR them either since this causes hydration errors.
const remountScripts = (id: string) => {
  const embed = document.getElementById(id);
  if (!embed) {
    return;
  }
  const scripts = Array.from(embed.querySelectorAll('script')) as HTMLScriptElement[];
  let i = 0;
  for (const script of scripts) {
    const attributes = Array.from(script.attributes);

    // create a new script tag thats a copy of the old one
    const newScript = document.createElement('script');
    attributes.forEach((attribute) => {
      newScript.setAttribute(attribute.name, attribute.value);
    });
    newScript.innerHTML = script.innerHTML;
    newScript.id = `html-embed-script-${id}-${i++}`;
    const existingScript = document.getElementById(newScript.id);
    if (existingScript) {
      // remove the existing script to avoid duplicates
      existingScript.remove();
    }
    embed.appendChild(newScript);
  }
};
